import type { QueryData, User, Filter } from './types'
import type { TableColumnType, InputRef } from 'antd'
import type { FilterDropdownProps, SorterResult } from 'antd/es/table/interface'

import { SearchOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { DatePicker, Button, Card, Input, Space, Table } from 'antd'
import cx from 'classnames'
import { format } from 'date-fns'
import React, { useRef, useState } from 'react'

import $api from '../../../API'

import { ExpandedRow } from './expanded-row'
import { currencyFormatter } from './functions'
import { BalanceType } from './types'

export function PinnacleFeedPage(): React.JSX.Element {
  const [filter, setFilter] = useState<Filter>({
    sort: 'date',
    order: 'DESC',
    page: 0,
    perPage: 30
  })
  const [dateFilter, setDateFilter] = useState<Date | null>(null)
  const query = useQuery<unknown, unknown, QueryData, ['pinnacle-feed-data', Filter]>({
    queryKey: ['pinnacle-feed-data', filter],

    async queryFn() {
      const { data } = await $api.get<QueryData>('pinnacle/data', {
        params: filter
      })

      return {
        users: data.users,
        count: data.count
      }
    }
  })
  const searchInput = useRef<InputRef>(null)

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: keyof Filter
  ): void => {
    confirm()
    setFilter(f => ({
      ...f,
      [dataIndex]: dataIndex === 'date' ? dateFilter : selectedKeys[0]
    }))
  }

  const handleDate = (date?: Date): void => {
    if (date) {
      setDateFilter(date)
    }
  }

  const handleReset = (
    clearFilters: () => void,
    dataIndex: keyof Filter,
    confirm: FilterDropdownProps['confirm']
  ): void => {
    clearFilters()
    setFilter(f => {
      const { [dataIndex]: _, ...rest } = f
      return rest as Filter
    })
    confirm()
  }

  const getColumnSearchProps = (dataIndex: keyof Filter): TableColumnType<User> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div
        style={{ padding: 8 }}
        onKeyDown={e => {
          e.stopPropagation()
        }}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={filter[dataIndex] as string}
          onChange={e => {
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }}
          onPressEnter={() => {
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type='primary'
            onClick={() => {
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }}
            icon={<SearchOutlined />}
            size='small'
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              if (clearFilters) {
                handleReset(clearFilters, dataIndex, confirm)
              }
            }}
            size='small'
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type='link'
            size='small'
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: () => (
      <SearchOutlined
        className={cx({
          'text-[#1890ff]': filter[dataIndex] !== undefined,
          'animate-pulse': query.isLoading && filter[dataIndex] !== undefined
        })}
      />
    ),
    onFilterDropdownOpenChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    }
  })

  return (
    <div className='flex flex-col font-lato font-normal gap-4 bg-lucent-background text-lucent-text rounded-lg p-6'>
      <div className='flex justify-between items-start'>
        <div className='flex items-center gap-6'>
          <h1 className='text-2xl'>Banking Detail</h1>
        </div>
      </div>
      <Card
        className='w-full rounded-xl'
        bodyStyle={{
          padding: '1rem'
        }}
      >
        <Table<User>
          scroll={{ x: 'max-content' }}
          size='small'
          rowKey='account_number'
          dataSource={query.data?.users}
          loading={query.isLoading}
          sortDirections={['ascend', 'descend']}
          pagination={{
            pageSize: filter.perPage,
            current: filter.page + 1,
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true,
            total: query.data?.count ?? 0,
            onChange: (page, perPage) => {
              setFilter(f => ({
                ...f,
                page: page - 1,
                perPage
              }))
            },
            showTotal(total, range) {
              return (
                <span className='text-sm text-gray-400'>
                  Showing {range[0]}–{range[1]} of {total}
                  {query.data && query.data.count !== total && ` • ${query.data.count} total`}
                </span>
              )
            }
          }}
          onChange={(
            _pagination,
            _filters,
            sorter: SorterResult<User> | Array<SorterResult<User>>,
            _extra
          ) => {
            const selectedSorted = Array.isArray(sorter) ? sorter[0] : sorter
            if (selectedSorted?.order) {
              setFilter(f => ({
                ...f,
                sort: selectedSorted.field as keyof User,
                order: selectedSorted.order === 'descend' ? 'DESC' : 'ASC'
              }))
            }
          }}
          expandable={{
            rowExpandable: (record: QueryData['users'][0]) => record.transactions.length > 0,
            expandedRowRender: (record: QueryData['users'][0]) => (
              <ExpandedRow row={record.transactions} />
            )
          }}
          columns={[
            {
              key: 'group_name',
              dataIndex: 'group_name',
              title: 'Group name',
              fixed: 'left',
              sorter: true,
              showSorterTooltip: false,
              className: 'show-default-sort text-xs [&_.ant-table-column-title]:text-center',
              ...getColumnSearchProps('group_name')
            },
            {
              key: 'group_number',
              dataIndex: 'group_number',
              title: 'Group number',
              fixed: 'left',
              sorter: true,
              showSorterTooltip: false,
              className: 'show-default-sort text-xs [&_.ant-table-column-title]:text-center',
              ...getColumnSearchProps('group_number')
            },
            {
              key: 'date',
              dataIndex: 'date',
              title: 'Date',
              render: (date: string) => format(new Date(date), 'MM/dd/yyyy'),
              sorter: true,
              showSorterTooltip: false,
              defaultSortOrder: 'descend',
              className: 'show-default-sort text-xs [&_.ant-table-column-title]:text-center',
              filterDropdown: ({ selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                  <DatePicker
                    onChange={e => {
                      handleDate(e?.toDate())
                    }}
                    allowClear
                  />
                  <Space>
                    <Button
                      type='primary'
                      onClick={() => {
                        handleSearch(selectedKeys as string[], confirm, 'date')
                      }}
                      icon={<SearchOutlined />}
                      size='small'
                      style={{ width: 90 }}
                    >
                      Search
                    </Button>
                    <Button
                      onClick={() => {
                        if (clearFilters) {
                          handleReset(clearFilters, 'date', confirm)
                        }
                      }}
                      size='small'
                      style={{ width: 90 }}
                    >
                      Reset
                    </Button>
                  </Space>
                </div>
              ),
              filterIcon: () => (
                <SearchOutlined
                  className={cx({
                    'text-[#1890ff]': filter.date !== undefined,
                    'animate-pulse': query.isLoading && filter.date !== undefined
                  })}
                />
              )
            },
            {
              key: 'account_number',
              dataIndex: 'account_number',
              title: 'Account Number',
              sorter: true,
              showSorterTooltip: false,
              className: 'show-default-sort text-xs [&_.ant-table-column-title]:text-center',
              ...getColumnSearchProps('account_number')
            },
            ...[
              'total_credits',
              'total_debits',
              'opening_ledger',
              'closing_ledger',
              'opening_available'
            ].map(key => ({
              key,
              dataIndex: key,
              title: BalanceType[key as keyof typeof BalanceType],
              sorter: true,
              showSorterTooltip: false,
              className: 'show-default-sort text-xs [&_.ant-table-column-title]:text-center',
              render: (value: number | null) => {
                if (value !== null) {
                  return currencyFormatter(value / 100)
                }
                return '-'
              }
            })),
            {
              key: 'file_name',
              dataIndex: 'file_name',
              title: 'File name'
            },
            {
              key: 'date',
              dataIndex: 'date',
              title: 'File date',
              className: 'show-default-sort text-xs [&_.ant-table-column-title]:text-center',
              render: (date: string) => format(new Date(date), 'MM/dd/yyyy HH:mm')
            },
            {
              key: 'sender',
              dataIndex: 'sender',
              title: 'Sender'
            }
          ]}
        />
      </Card>
    </div>
  )
}
